/**
 * 本分公司人员（/branch-staff）—— 经理端的人员与账号管理页。
 *
 * 【需求来源与模式（2026-10-27 定稿 2a）】
 *   「本分公司人员」是权限目录里的一个普通页面权限点：管理员在权限矩阵 / 账号勾选树里
 *   授予谁，谁就能管理自己所属分公司的人员与账号 —— 与其他页面同一套配置、同一份判定。
 *   经理在这里可以：
 *     · 看本分公司账号与员工名单（只返回必要字段，不复用全公司账号列表的敏感投影）；
 *     · 新建员工档案 + 登录账号：选一个一线基础岗位，可选附加权限（只能从**自己有效权限
 *       里**的键里勾，服务端按「新账号完整有效权限 ⊆ 我的有效权限」实时复核，超了整体拒绝）；
 *     · 停用 / 启用本分公司一线岗位账号（不能碰自己、管理岗、以及另一位持有本页权限的人）。
 *   刻意不做：改现有账号资料、调岗、改角色、改权限、删除账号、跨分公司操作、把
 *   /branch-staff 或财务 / 人事等高风险区域发给任何新账号（服务端硬编码拒绝，不套娃）。
 *
 * 【为什么前端只有引导作用】真正的边界全部在服务端（backend/branch_delegation.py）：
 *   入口权限、分公司有效性、岗位白名单、附加键合法性、权限上限，都在 POST 时实时重算。
 *
 * 【页面状态怎么读】GET /delegation/context 一次性拿到：分公司、基础岗位清单、
 *   可下放权限目录（grantable = 我自己有效权限里的页面与子键，已剔除高风险区域）。
 *   403 一律按「未获授权」整页提示处理 —— 那说明账号没有被授予这个页面权限，
 *   页面本不该出现在菜单里。
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useApp } from "../context/AppContext";
import { departmentLabel } from "../lib/departmentLabel";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "../components/ui/sonner";
import { EmptyState, ErrorState, TableSkeleton } from "../components/States";
import { groupModules } from "../lib/modPerms";
import { postEmployeeWithDuplicateCheck } from "../lib/employeeCreate";
import {
  Ban, Building2, CheckCircle2, Loader2, Lock, Plus, RotateCw, Search, ShieldCheck, UserPlus, Users,
} from "lucide-react";

const ROLE_LABEL = {
  member: { zh: "员工", en: "Member" },
  sales: { zh: "销售", en: "Sales" },
  cdc: { zh: "设计顾问", en: "CDC" },
  aftersales: { zh: "售后", en: "Aftersales" },
  telesales: { zh: "电销", en: "Telesales" },
  b2b_sales: { zh: "B2B 销售", en: "B2B Sales" },
};

const EMPTY_FORM = { username: "", name: "", nameZh: "", phone: "", email: "", password: "", baseRole: "" };

export default function BranchStaff() {
  const { lang, api } = useApp();
  const zh = lang === "zh";
  const T = useCallback((z, e) => (zh ? z : e), [zh]);

  const [ctx, setCtx] = useState(null);      // null = 加载中；false = 未获授权
  const [users, setUsers] = useState(null);
  const [employees, setEmployees] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("users");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [keys, setKeys] = useState(() => new Set());  // 附加权限键（可选；上限由服务端实时复核）
  const [pending, setPending] = useState(null);  // { username, disabled } 等待确认的停用 / 启用

  const load = useCallback(async () => {
    setErr("");
    try {
      const c = await api.get("/delegation/context");
      setCtx(c.data);
    } catch (e) {
      // 403 = 该账号没有被授予「本分公司人员」页面权限。这不是「加载失败」，是根本不该进这个页面。
      if (e?.response?.status === 403) { setCtx(false); return; }
      setErr(e?.response?.data?.detail || e.message);
      setCtx(null);
      return;
    }
    try {
      const [u, emp] = await Promise.all([api.get("/delegation/users"), api.get("/delegation/employees")]);
      setUsers(u.data?.items || []);
      setEmployees(emp.data?.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]); // eslint-disable-line

  // ---- 可下放权限树：按目录分组（与权限矩阵同一套 groupModules），子键挂在其父页面下 ----
  const groups = useMemo(() => groupModules(ctx?.grantable?.pages || [], zh), [ctx, zh]);
  const subsByParent = useMemo(() => {
    const o = {};
    (ctx?.grantable?.subs || []).forEach((s) => { (o[s.parent] = o[s.parent] || []).push(s); });
    return o;
  }, [ctx]);
  const toggleKey = (k) => setKeys((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const togglePageWithSubs = (m) => {
    setKeys((s) => {
      const n = new Set(s);
      if (n.has(m.key)) { n.delete(m.key); (subsByParent[m.key] || []).forEach((x) => n.delete(x.key)); }
      else { n.add(m.key); (subsByParent[m.key] || []).forEach((x) => n.add(x.key)); }
      return n;
    });
  };

  // 搜索只在前端过滤：本分公司人员规模很小，后端也没有分页需求（一次最多 100 条）。
  const kw = q.trim().toLowerCase();
  const hit = (row, fields) => !kw || fields.some((f) => String(row[f] ?? "").toLowerCase().includes(kw));
  const userList = useMemo(() => (users || []).filter((u) => hit(u, ["username", "name", "nameZh", "role", "email", "phone"])), [users, kw]);
  const empList = useMemo(() => (employees || []).filter((e) => hit(e, ["id", "name", "nameZh", "department", "position", "username"])), [employees, kw]);

  const resetForm = () => { setForm(EMPTY_FORM); setKeys(new Set()); };

  const submitAdd = async () => {
    if (busy) return;
    if (!form.username.trim() || !form.name.trim() || !form.baseRole) {
      toast.error(T("请填写用户名、姓名并选择基础岗位", "Username, name and base role are required"));
      return;
    }
    setBusy("add");
    try {
      const payload = {
        username: form.username.trim(), name: form.name.trim(), nameZh: form.nameZh.trim() || form.name.trim(),
        phone: form.phone.trim(), email: form.email.trim(), password: form.password,
        baseRole: form.baseRole, keys: [...keys],
      };
      const result = await postEmployeeWithDuplicateCheck(api, "/delegation/users", payload, zh);
      if (result.cancelled) return;
      const { data } = result.response;
      // 经理设置的初始密码只在这里出现一次（服务端只存哈希），必须当屏提示务必转告本人。
      const roleLabel = ctx?.baseRoles?.find((r) => r.key === data.role)?.[zh ? "zh" : "en"] || ROLE_LABEL[data.role]?.[zh ? "zh" : "en"] || data.role;
      toast.success(T(
        `已创建 ${data.username}（${roleLabel}${data.permissions?.length ? `，附加 ${data.permissions.length} 项权限` : ""}）· 初始密码已按你填写的内容设置，请转告本人并在首次登录时修改`,
        `Created ${data.username} (${roleLabel}${data.permissions?.length ? `, ${data.permissions.length} extra permission(s)` : ""}). Share the initial password with them; they must change it at first login.`,
      ));
      setAddOpen(false);
      resetForm();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(""); }
  };

  const applyState = async () => {
    if (!pending || busy) return;
    const { username, disabled } = pending;
    setPending(null);
    setBusy(`state:${username}`);
    try {
      await api.post(`/delegation/users/${encodeURIComponent(username)}/state`, { disabled });
      toast.success(disabled ? T(`已停用 ${username}`, `${username} disabled`) : T(`已启用 ${username}`, `${username} enabled`));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(""); }
  };

  // ---- 未获授权：不是错误，是权限边界。不渲染任何人员数据。 ----
  if (ctx === false) {
    return (
      <div className="space-y-4" data-testid="branch-staff-page">
        <h1 className="text-2xl font-bold text-slate-900">{T("本分公司人员", "My Branch Staff")}</h1>
        <div className="rounded-2xl border border-slate-200 bg-white">
          <EmptyState icon={Lock} testId="branch-staff-forbidden"
            title={T("未获授权管理本分公司人员", "Not authorized to manage branch staff")}
            hint={T("该能力由管理员在权限配置（角色矩阵或账号权限）里授予「本分公司人员」页面权限后生效。如需为本分公司新建员工或账号，请联系管理员。",
              "This capability is granted by an administrator via the permission matrix (the My Branch Staff page permission). Ask them to enable it for your account.")} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="branch-staff-page">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-keep-title>
            <Users className="h-6 w-6 text-brand-500" />{T("本分公司人员", "My Branch Staff")}
          </h1>
          {ctx && (
            <p className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="branch-staff-branch">
              <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{T("所属分公司", "Branch")}：<b className="text-slate-700">{ctx.branch}</b></span>
              <span className="inline-flex items-center gap-1">
                <ShieldCheck className="h-3.5 w-3.5" />
                {T("可下放权限上限", "Grant ceiling")}：
                <b className="text-slate-700">{T("以你自己的权限为上限", "limited to your own permissions")}</b>
              </span>
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          <div className="relative w-full sm:w-56">
            <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400" />
            <Input data-testid="branch-staff-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={T("搜索姓名 / 用户名 / 联系方式…", "Search name / username…")} className="h-8 pl-7 text-sm" />
          </div>
          <Button data-testid="branch-staff-refresh" variant="outline" size="sm" className="h-8 gap-1"
            disabled={!!busy} onClick={load}>
            <RotateCw className="h-3.5 w-3.5" />{T("刷新", "Refresh")}
          </Button>
          <Button data-testid="branch-staff-add-btn" size="sm" className="h-8 gap-1"
            disabled={!!busy}
            onClick={() => { resetForm(); setAddOpen(true); }}>
            <UserPlus className="h-4 w-4" />{T("新建员工与账号", "New staff account")}
          </Button>
        </div>
      </div>

      {err && <ErrorState title={T("加载失败", "Failed to load")} detail={err} onRetry={load} zh={zh} testId="branch-staff-error" />}

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="flex items-center gap-1 px-3 pt-2 border-b border-slate-100">
          {[{ k: "users", label: T("账号", "Accounts"), n: users?.length }, { k: "employees", label: T("员工档案", "Employees"), n: employees?.length }].map((t) => (
            <button key={t.k} type="button" data-testid={`branch-staff-tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`px-3 py-2 text-sm rounded-t-lg border-b-2 -mb-px transition-colors ${tab === t.k ? "border-brand-500 text-brand-600 font-semibold" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              {t.label}{t.n != null && <span className="ml-1 text-[11px] text-slate-400">{t.n}</span>}
            </button>
          ))}
          <span className="ml-auto text-[11px] text-slate-400 pb-2 hidden sm:inline">
            {T("只能查看和管理本分公司；管理岗与总部账号请由管理员处理", "Own branch only; privileged accounts are handled by administrators")}
          </span>
        </div>

        {tab === "users" && (
          <div data-testid="branch-staff-users">
            <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 text-[11px] font-semibold text-slate-500">
              <span className="flex-1 min-w-0">{T("姓名 / 用户名", "Name / Username")}</span>
              <span className="w-28 shrink-0">{T("岗位", "Role")}</span>
              <span className="w-40 shrink-0 hidden md:block">{T("联系方式", "Contact")}</span>
              <span className="w-20 shrink-0">{T("状态", "Status")}</span>
              <span className="w-24 shrink-0 text-right">{T("操作", "Actions")}</span>
            </div>
            {users === null && !err && <TableSkeleton rows={5} cols={5} testId="branch-staff-users-skeleton" />}
            {users !== null && userList.length === 0 && (
              <EmptyState compact icon={Users} testId="branch-staff-users-empty"
                title={kw ? T("没有匹配的账号", "No matching accounts") : T("本分公司还没有账号", "No accounts in this branch")}
                hint={kw ? T("换个关键词试试", "Try another keyword") : T("点右上角「新建员工与账号」为分公司添加第一位员工。", "Use New staff account to add the first one.")} />
            )}
            {userList.map((u) => (
              <div key={u.username} data-testid={`branch-staff-user-${u.username}`}
                className="flex items-center gap-3 px-4 py-2.5 border-t border-slate-100 hover:bg-slate-50/60">
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-slate-800 truncate">{u.name || u.nameZh || u.username}</span>
                  <span className="block text-[11px] text-slate-400 truncate">{u.username}</span>
                </span>
                <span className="w-28 shrink-0 text-xs text-slate-600">{ROLE_LABEL[u.role]?.[zh ? "zh" : "en"] || u.role}</span>
                <span className="w-40 shrink-0 text-xs text-slate-500 truncate hidden md:block">{[u.phone, u.email].filter(Boolean).join(" · ") || "—"}</span>
                <span className="w-20 shrink-0">
                  <span data-testid={`branch-staff-user-state-${u.username}`}
                    className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${u.disabled ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-700"}`}>
                    {u.disabled ? <Ban className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                    {u.disabled ? T("已停用", "Disabled") : T("启用中", "Active")}
                  </span>
                </span>
                <span className="w-24 shrink-0 text-right">
                  {u.canManage ? (
                    <Button size="sm" variant="outline" data-testid={`branch-staff-toggle-${u.username}`}
                      disabled={busy === `state:${u.username}`}
                      className={`h-7 px-2 text-xs gap-1 ${u.disabled ? "" : "text-rose-600 border-rose-200 hover:bg-rose-50"}`}
                      onClick={() => setPending({ username: u.username, disabled: !u.disabled })}>
                      {busy === `state:${u.username}` ? <Loader2 className="h-3 w-3 animate-spin" /> : u.disabled ? <CheckCircle2 className="h-3 w-3" /> : <Ban className="h-3 w-3" />}
                      {u.disabled ? T("启用", "Enable") : T("停用", "Disable")}
                    </Button>
                  ) : (
                    <span className="text-[11px] text-slate-300" title={T("本人 / 管理岗 / 持有本页权限的账号不可在此操作", "Self, privileged or fellow-manager accounts cannot be managed here")}>—</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}

        {tab === "employees" && (
          <div data-testid="branch-staff-employees">
            <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 text-[11px] font-semibold text-slate-500">
              <span className="w-24 shrink-0">{T("员工编号", "Employee ID")}</span>
              <span className="flex-1 min-w-0">{T("姓名", "Name")}</span>
              <span className="w-40 shrink-0 hidden md:block">{T("部门", "Department")}</span>
              <span className="w-32 shrink-0">{T("关联账号", "Account")}</span>
              <span className="w-20 shrink-0">{T("状态", "Status")}</span>
            </div>
            {employees === null && !err && <TableSkeleton rows={5} cols={5} testId="branch-staff-employees-skeleton" />}
            {employees !== null && empList.length === 0 && (
              <EmptyState compact icon={Users} testId="branch-staff-employees-empty"
                title={kw ? T("没有匹配的员工", "No matching employees") : T("本分公司还没有员工档案", "No employee records")}
                hint={kw ? T("换个关键词试试", "Try another keyword") : T("新建员工与账号时会自动建档，无需单独录入。", "Creating a staff account also creates the employee record.")} />
            )}
            {empList.map((e) => (
              <div key={e.id} data-testid={`branch-staff-emp-${e.id}`}
                className="flex items-center gap-3 px-4 py-2.5 border-t border-slate-100 hover:bg-slate-50/60">
                <code className="w-24 shrink-0 text-[11px] text-slate-400">{e.id}</code>
                <span className="flex-1 min-w-0 text-sm text-slate-800 truncate">{e.name || e.nameZh || "—"}</span>
                <span className="w-40 shrink-0 text-xs text-slate-500 truncate hidden md:block">{departmentLabel(e.department, { id: e.department }) || "—"}</span>
                <span className="w-32 shrink-0 text-xs text-slate-500 truncate">{e.username || "—"}</span>
                <span className="w-20 shrink-0 text-[11px] text-slate-500">{e.status || "—"}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 新建员工与账号 */}
      <Dialog open={addOpen} onOpenChange={(o) => { if (!o) { setAddOpen(false); resetForm(); } }}>
        <DialogContent data-testid="branch-staff-add-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="h-4 w-4 text-brand-500" />{T("新建员工与账号", "New staff account")}</DialogTitle>
            <DialogDescription>
              {T("一次同时建立员工档案与登录账号。附加权限只能从你自己有的权限里勾选，服务端会按「新账号权限不超过你」实时复核；首次登录会强制修改初始密码。",
                "Creates the employee record and login account together. Extra permissions can only be picked from your own; the server re-checks that the new account never exceeds you. The initial password must be changed at first login.")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-slate-500">{T("姓名（英文）*", "Name *")}</span>
              <Input data-testid="branch-staff-name" value={form.name} autoFocus className="h-9 mt-1"
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Jane Doe" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">{T("姓名（中文）", "Name (ZH)")}</span>
              <Input data-testid="branch-staff-name-zh" value={form.nameZh} className="h-9 mt-1"
                onChange={(e) => setForm((f) => ({ ...f, nameZh: e.target.value }))} placeholder={T("留空则与英文名相同", "Defaults to the English name")} />
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">{T("登录用户名 *", "Username *")}</span>
              <Input data-testid="branch-staff-username" value={form.username} className="h-9 mt-1"
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} placeholder="jane.doe" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">{T("基础岗位 *", "Base role *")}</span>
              <Select value={form.baseRole} onValueChange={(v) => setForm((f) => ({ ...f, baseRole: v }))}>
                <SelectTrigger data-testid="branch-staff-base-role" className="h-9 mt-1"><SelectValue placeholder={T("请选择", "Select")} /></SelectTrigger>
                <SelectContent>
                  {(ctx?.baseRoles || []).map((r) => (
                    // creatable=false：该岗位的默认权限超出经理自己的权限，建号必被服务端 409。
                    // 不禁用就变成「看得见、点得下去、每次都被拒」，经理还会以为是系统故障；
                    // 这里置灰并在右侧写明缺什么，把「上限」这件事在选之前就说清楚。
                    <SelectItem key={r.key} value={r.key} disabled={r.creatable === false}
                      data-testid={`branch-staff-role-opt-${r.key}`}>
                      {r.zh || ROLE_LABEL[r.key]?.zh || r.key}{zh ? "" : (r.en ? ` · ${r.en}` : "")}
                      {r.creatable === false && (
                        <span className="ml-2 text-[10px] text-amber-600">
                          {T("超出你的权限", "beyond your permissions")}
                          {r.missingPages?.length ? `（${T("缺", "missing")} ${r.missingPages.length} ${T("个页面", "page(s)")}）` : ""}
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">{T("电话", "Phone")}</span>
              <Input data-testid="branch-staff-phone" value={form.phone} className="h-9 mt-1"
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="(555) 010-0100" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">{T("邮箱", "Email")}</span>
              <Input data-testid="branch-staff-email" value={form.email} className="h-9 mt-1"
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="jane@example.com" />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-xs text-slate-500">{T("初始密码 *", "Initial password *")}</span>
              <Input data-testid="branch-staff-password" type="text" value={form.password} className="h-9 mt-1 font-mono"
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder={T("至少 8 位，需当面或私下转告本人", "At least 8 characters; share it privately")} />
            </label>
            {/* 附加权限（可选）：只列「我自己有的」页面与按钮；高风险区域服务端也不收。
                交互口径与权限矩阵一致：父页面勾上才能勾子按钮，取消父页面连带取消全部子键。 */}
            {(groups || []).length > 0 && (
              <div className="sm:col-span-2" data-testid="branch-staff-keys-section">
                <p className="text-xs text-slate-500">{T("附加权限（可选）", "Extra permissions (optional)")}
                  <span className="ml-2 text-[10px] text-slate-400">{T("超出你权限的勾选会被服务端拒绝", "Anything beyond your own permissions is rejected")}</span>
                </p>
                <div className="mt-1 max-h-56 overflow-y-auto rounded-lg border border-slate-200 p-2 space-y-2 bg-slate-50/50">
                  {groups.map((g, gi) => (
                    <div key={g.label} data-testid={`branch-staff-keys-group-${gi}`}>
                      {g.label && <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{g.label}</p>}
                      <div className="space-y-1">
                        {g.items.map((m) => {
                          const subs = subsByParent[m.key] || [];
                          const on = keys.has(m.key);
                          // 【2026-10-28 验收修复】grantable === false 表示：这个页面你本人有，
                          // 但它下面还有若干按钮你没有 —— 勾父页面会在账号级授予层把那些按钮
                          // 一起判 True，提交必然被服务端上限闸门 409 拒绝。所以这里置灰并说明
                          // 缺什么，而不是让经理点一个注定失败的框。
                          // 与后端 _grantable_tree / create_staff 判定同一口径。
                          const blocked = m.grantable === false;
                          return (
                            <div key={m.key}>
                              <label className={`flex items-center gap-1.5 text-[12px] ${blocked ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                                title={blocked
                                  ? T(`你缺少该页面下的 ${m.missingSubsCount || (m.missingSubs || []).length} 个按钮权限，勾选会被拒绝`,
                                      `You lack ${m.missingSubsCount || (m.missingSubs || []).length} button permission(s) under this page; selecting it would be rejected`)
                                  : (zh ? m.zh : m.en)}>
                                <input type="checkbox" data-testid={`branch-staff-key-${m.key}`} checked={on} disabled={blocked}
                                  onChange={() => togglePageWithSubs(m)} className="accent-brand-600 shrink-0" />
                                <span className={on ? "text-slate-800" : "text-slate-600"}>{zh ? m.zh : m.en}</span>
                                {blocked && (
                                  <span className="text-[10px] text-amber-600" data-testid={`branch-staff-key-blocked-${m.key}`}>
                                    {T("缺", "missing")} {m.missingSubsCount || (m.missingSubs || []).length} {T("个按钮", "button(s)")}
                                  </span>
                                )}
                              </label>
                              {on && subs.length > 0 && (
                                <div className="ml-5 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                                  {subs.map((sm) => (
                                    <label key={sm.key} className="inline-flex items-center gap-1 text-[11px] cursor-pointer" title={zh ? sm.zh : sm.en}>
                                      <input type="checkbox" data-testid={`branch-staff-key-${sm.key}`} checked={keys.has(sm.key)}
                                        onChange={() => toggleKey(sm.key)} className="accent-brand-600 scale-90" />
                                      <span className={keys.has(sm.key) ? "text-slate-700" : "text-slate-400"}>{zh ? sm.zh : sm.en}</span>
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {form.baseRole && (
              <p className="sm:col-span-2 text-[11px] text-slate-500 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2" data-testid="branch-staff-role-summary">
                {(() => {
                  const r = (ctx?.baseRoles || []).find((x) => x.key === form.baseRole);
                  const label = r?.[zh ? "zh" : "en"] || ROLE_LABEL[form.baseRole]?.[zh ? "zh" : "en"] || form.baseRole;
                  return keys.size
                    ? T(`岗位：${label}；将额外授予 ${keys.size} 项权限。`, `Role: ${label}; ${keys.size} extra permission(s).`)
                    : T(`岗位：${label}；本次只给该岗位的默认权限。`, `Role: ${label}; role defaults only.`);
                })()}
              </p>
            )}
            {/* 一个岗位都开不了时单独说明：这是「你的账号权限还不够」，不是页面坏了。
                不写清原因，经理只会反复试岗、反复被 409，然后来问「为什么加不了人」。 */}
            {(ctx?.baseRoles || []).length > 0 && (ctx?.baseRoles || []).every((r) => r.creatable === false) && (
              <p className="sm:col-span-2 text-[11px] text-amber-700 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2"
                data-testid="branch-staff-no-creatable-role">
                {T("你目前没有任何岗位可以开号：每个一线岗位的默认权限都超出了你自己的权限。请管理员在权限配置里给你补上对应页面，或由总部代为建号。",
                  "You cannot create any role yet: every frontline role's defaults exceed your own permissions. Ask an administrator to grant you the matching pages, or have HQ create the account.")}
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" data-testid="branch-staff-add-cancel" onClick={() => { setAddOpen(false); resetForm(); }}>
              {T("取消", "Cancel")}
            </Button>
            <Button data-testid="branch-staff-add-save" disabled={busy === "add"} onClick={submitAdd} className="gap-1">
              {busy === "add" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}{T("创建", "Create")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 停用 / 启用确认：破坏性操作用同一语义色，并写清影响范围 */}
      <AlertDialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
        <AlertDialogContent data-testid="branch-staff-state-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pending?.disabled ? T("确认停用该账号？", "Disable this account?") : T("确认启用该账号？", "Enable this account?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {/* 注意：这里的 two-arg T() 两个参数都会先求值。pending 为 null（弹窗关闭态）时
                  写 `pending.username` 会直接抛 TypeError 把整页送进错误边界 —— 统一用可选链。 */}
              {pending?.disabled
                ? T(`停用后 ${pending?.username || ""} 立即无法登录；权限配置保持不变，重新启用后按当前配置恢复访问。`,
                    `${pending?.username || ""} cannot sign in once disabled. Permission settings are kept; access follows them again after re-enable.`)
                : T(`启用后 ${pending?.username || ""} 可以重新登录，权限按当前配置生效。`,
                    `${pending?.username || ""} can sign in again with their current permissions.`)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="branch-staff-state-cancel">{T("取消", "Cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="branch-staff-state-confirm"
              className={pending?.disabled ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
              onClick={applyState}>
              {pending?.disabled ? T("确认停用", "Disable") : T("确认启用", "Enable")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
